import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import ScrollToTop from '../components/ScrollToTop';
import VideoPlayer from '../components/VideoPlayer';
import InteractiveDemo from '../components/InteractiveDemo';
import TeamMember from '../components/TeamMember';
import GitHubButton from '../components/GitHubButton';

// Import SVG as component
import { ReactComponent as MetaNodeSVG } from '../assets/svg/metanode.svg';

const PageContainer = styled.div`
  padding-top: 2rem;
`;

const HeroSection = styled.section`
  background: linear-gradient(to right, #673ab7, #9c27b0);
  color: white;
  padding: 4rem 2rem;
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const Title = styled(motion.h1)`
  font-size: 3rem;
  margin-bottom: 1.5rem;
`;

const Subtitle = styled(motion.h2)`
  font-size: 1.5rem;
  margin-bottom: 2rem;
  font-weight: 400;
`;

const ContentSection = styled.section`
  max-width: 1200px;
  margin: 3rem auto;
  padding: 0 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  margin-bottom: 1.5rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 4px;
    background: #673ab7;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const Card = styled(motion.div)`
  background: white;
  border-radius: 10px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #673ab7;
`;

const CardContent = styled.p`
  line-height: 1.6;
  color: #555;
`;

const DiagramContainer = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 3rem auto;
`;

const DiagramTitle = styled.h3`
  text-align: center;
  margin-bottom: 2rem;
  font-size: 1.8rem;
  color: #333;
`;

const AnimationContainer = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 3rem auto;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
`;

const CodeBlock = styled.pre`
  background-color: #1e1e1e;
  color: #d4d4d4;
  border-radius: 5px;
  padding: 1.5rem;
  margin: 1.5rem 0;
  overflow-x: auto;
  font-family: 'Consolas', 'Monaco', 'Andale Mono', monospace;
`;

const Code = styled.code`
  font-family: 'Consolas', 'Monaco', 'Andale Mono', monospace;
`;

const MetaNode = () => {
  // Sample API demo request/response for MetaNode
  const apiDemoData = {
    request: `{
  "contract": "MyToken",
  "function": "deploy",
  "arguments": {
    "name": "MetaCoin",
    "symbol": "MTC",
    "initialSupply": 1000000
  },
  "vPodConfig": {
    "resources": {
      "memory": "512Mi",
      "cpu": "100m"
    },
    "security": "high"
  }
}`,
    response: `{
  "status": "success",
  "data": {
    "contractAddress": "0x7a58c0b21bfF6B5C5957822f6c9f2e3D4A1e3D47",
    "transactionHash": "0x42e89b5682332e7d21392d693e395a264c018da10956bb13526b0d66ef66ce23",
    "deployedAt": "2023-10-12T15:30:22Z",
    "vPodId": "vpod-28491f3a",
    "gasUsed": 2187654
  }
}`
  };
  
  // Sample SDK demo code
  const sdkDemoCode = `// Initialize MetaNode client
const metanode = require('metanode-sdk');
const client = await metanode.createClient({
  nodeUrl: 'https://mainnet.metanode.network',
  credentials: {
    privateKey: process.env.PRIVATE_KEY
  }
});

// Deploy a smart contract
const contract = await client.deployContract({
  name: 'MetaCoin',
  source: './contracts/MetaCoin.sol',
  vPodConfig: {
    resources: {
      memory: '512Mi',
      cpu: '100m'
    },
    security: 'high'
  }
});

console.log(\`Contract deployed at \${contract.address}\`);`;

  return (
    <PageContainer>
      <ScrollToTop background="#673ab7" />
      <HeroSection>
        <HeroContent>
          <Title
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            MetaNode Blockchain
          </Title>
          <Subtitle
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Blockchain & dApp Deployment Infrastructure with Ethereum-grade Security
          </Subtitle>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <SectionTitle>Overview</SectionTitle>
        <p>
          MetaNode is a revolutionary blockchain infrastructure that combines the security of Ethereum 
          with innovative vPod container technology. The platform enables seamless deployment of 
          decentralized applications while maintaining the highest security standards.
        </p>
        
        <DiagramContainer>
          <DiagramTitle>MetaNode Architecture</DiagramTitle>
          <MetaNodeSVG width="100%" height="auto" />
        </DiagramContainer>
        
        <AnimationContainer>
          <VideoPlayer 
            src="https://youtu.be/XhBVKR5SELY"
            title="MetaNode Blockchain"
          />
        </AnimationContainer>
        
        <GitHubButton url="https://github.com/GlobalSushrut/metanode-sdk">
          View MetaNode SDK on GitHub
        </GitHubButton>
        
        <SectionTitle>Core Components</SectionTitle>
        <Grid>
          <Card
            whileHover={{ y: -10 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <CardTitle>Blockchain Core</CardTitle>
            <CardContent>
              The heart of MetaNode, providing Ethereum-compatible consensus mechanisms
              with enhanced performance. Supports EVM compatibility with extended features
              specific to MetaNode's security architecture.
            </CardContent>
          </Card>
          
          <Card
            whileHover={{ y: -10 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <CardTitle>vPod Containers</CardTitle>
            <CardContent>
              Virtualized secure deployment environments that isolate dApp execution
              while maintaining blockchain integrity. vPods provide a sandboxed environment
              that prevents malicious code from compromising the network.
            </CardContent>
          </Card>
          
          <Card
            whileHover={{ y: -10 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <CardTitle>Security Layer</CardTitle>
            <CardContent>
              Military-grade security protocols ensure data integrity and 
              transaction validity. The security layer implements advanced cryptographic
              techniques to protect against various attack vectors.
            </CardContent>
          </Card>
        </Grid>
        
        <SectionTitle>Interactive Demo</SectionTitle>
        <p>
          Explore the MetaNode API and SDK through our interactive demo. Deploy contracts, interact with the blockchain, and see real-time responses:
        </p>
        
        <InteractiveDemo
          tabs={[
            { id: 'api', label: 'REST API', icon: 'api' },
            { id: 'sdk', label: 'JavaScript SDK', icon: 'code' },
            { id: 'cli', label: 'CLI', icon: 'terminal' }
          ]}
          defaultTab="api"
          apiRequestData={apiDemoData.request}
          apiResponseData={apiDemoData.response}
          sdkCode={sdkDemoCode}
          cliCommand={`metanode deploy --contract MyToken --args '{"name":"MetaCoin","symbol":"MTC","initialSupply":1000000}' --vpod-config high`}
          accentColor="#673ab7"
        />
        
        <SectionTitle>Key Features</SectionTitle>
        <Grid>
          <Card
            whileHover={{ y: -10 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <CardTitle>Scalable Infrastructure</CardTitle>
            <CardContent>
              Handles thousands of transactions per second with minimal latency.
              The architecture allows horizontal scaling to accommodate growing demand
              without compromising performance.
            </CardContent>
          </Card>
          
          <Card
            whileHover={{ y: -10 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <CardTitle>Cross-Chain Compatibility</CardTitle>
            <CardContent>
              Seamless integration with Ethereum and other major blockchain networks.
              Built-in bridges facilitate asset transfers and interoperability between
              different blockchain ecosystems.
            </CardContent>
          </Card>
          
          <Card
            whileHover={{ y: -10 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <CardTitle>Developer-Friendly Tools</CardTitle>
            <CardContent>
              Comprehensive SDK and CLI tools for easy development and deployment.
              Includes debugging tools, monitoring dashboards, and simulation environments
              to streamline the development process.
            </CardContent>
          </Card>
        </Grid>
        
        <SectionTitle>Use Cases</SectionTitle>
        <ul>
          <li>Financial DeFi Applications requiring high security and transaction integrity</li>
          <li>Enterprise Blockchain Solutions with confidential transaction requirements</li>
          <li>NFT Marketplaces with enhanced metadata processing capabilities</li>
          <li>Supply Chain Management Systems with cryptographic verification</li>
          <li>Decentralized Identity Solutions leveraging MetaNode's security framework</li>
          <li>Gaming Platforms requiring high transaction throughput and low latency</li>
        </ul>
      </ContentSection>

      <ContentSection>
        <SectionTitle>Founder</SectionTitle>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <TeamMember
            name="Umesh Adhikari"
            role="Founder & Lead Architect"
            image="/assets/images/team/umesh-adhikari.jpg"
            bio="Umesh Adhikari is a visionary technologist, philosopher, and self-taught innovator, forging new frontiers where mathematics, artificial intelligence, and decentralized systems converge. With roots grounded in determination and a spirit shaped by resilience, Umesh embodies the rare balance of humility and brilliance. From the bustling kitchens of his early days to the deep silence of code and theory, his journey is a testament to transformation — not just personal, but generational. Fueled by curiosity and an unshakable belief in building what the world has yet to imagine, Umesh is the mind behind frameworks like the Advanced Unified Intelligence Framework (AUIF) and Transition Numbers Theory — pioneering languages of logic that challenge the boundaries of science, computation, and consciousness itself."
            tags={['Blockchain Architecture', 'Smart Contracts', 'Decentralized Systems']}
            socials={[
              { platform: 'github', url: 'https://github.com/GlobalSushrut' },
              { platform: 'linkedin', url: '#' },
              { platform: 'email', url: 'mailto:umeshlamton@gmail.com' }
            ]}
            accentColor="#673ab7"
          />
        </div>
      </ContentSection>
    </PageContainer>
  );
};

export default MetaNode;
